/**
 * ProfileCompletionGate — Wave A6 Chunk 2 (Task 2.5).
 *
 * Nudges a freshly-authenticated, name-less user to /complete-profile exactly
 * once per browser session. Non-jailing: CompleteProfile's "skip for now" sets
 * the dismiss flag (useProfileComplete), so the redirect never repeats.
 *
 * Wave A6 Chunk 1 (B1): also provides the Suspense boundary for the entire
 * authenticated-routes subtree it wraps. Several lazy routes (Dashboard,
 * Players, Import, Formation) had no per-route <Suspense>, so a cold chunk
 * fetch on a deep route like /import suspended with no boundary and hung
 * indefinitely. This boundary sits inside Layout, so the sidebar/topbar shell
 * stays visible while a route chunk loads.
 */

import { Suspense, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProfileComplete } from '../hooks/useProfileComplete';
import { PageFallback } from './PageFallback';

export function ProfileCompletionGate({ children }: { children: React.ReactNode }) {
  const { needsCompletion } = useProfileComplete();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (needsCompletion && location.pathname !== '/complete-profile') {
      navigate('/complete-profile', { replace: true });
    }
  }, [needsCompletion, location.pathname, navigate]);

  return <Suspense fallback={<PageFallback />}>{children}</Suspense>;
}
